import {
  formatGuestPreviewCurrency,
  formatGuestPreviewDate,
  formatGuestPreviewId,
  getGuestPreviewInitials,
  getGuestPreviewTierClasses,
} from '@lib/utils';
import {
  AccommodationGuest,
  AccommodationGuestDetails,
} from '@/renderer/types/accommodation';
import { Mail, MessageCircle, Pencil, Phone, X } from 'lucide-react';
import React from 'react';
import { GuestPreviewQuickActionButton } from './GuestPreviewQuickActionButton';

interface GuestPreviewHeaderProps {
  guest: AccommodationGuest | null;
  details: AccommodationGuestDetails;
  onClose: () => void;
  onEdit: () => void;
}

export const GuestPreviewHeader: React.FC<GuestPreviewHeaderProps> = ({
  guest,
  details,
  onClose,
  onEdit,
}) => {
  const initials = guest
    ? getGuestPreviewInitials(guest.first_name, guest.last_name)
    : getGuestPreviewInitials(details.first_name, details.last_name);

  return (
    <div className="flex items-start justify-between border-b border-gray-200 bg-white px-6 py-5 sm:px-8">
      <div className="flex min-w-0 items-start gap-4">
        <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-gray-900 text-xl font-bold text-white">
          {initials}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-2xl font-bold text-gray-950">
              {details.first_name} {details.last_name}
            </h2>
            {details.tier_label && (
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getGuestPreviewTierClasses(details.tier_label)}`}
              >
                {details.tier_label}
              </span>
            )}
          </div>
          <p className="mt-2 text-sm text-gray-600">
            Guest ID:{' '}
            {guest
              ? formatGuestPreviewId(guest.id)
              : formatGuestPreviewId(details.id)}{' '}
            {'\u2022'} Member since{' '}
            {formatGuestPreviewDate(details.member_since, {
              month: 'short',
              year: 'numeric',
            })}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {details.contact && (
              <>
                <GuestPreviewQuickActionButton
                  url={`tel:${details.contact}`}
                  icon={<Phone size={15} />}
                  label="Call guest"
                />
                <GuestPreviewQuickActionButton
                  url={`https://wa.me/${details.contact.replace(/\D/g, '')}`}
                  icon={<MessageCircle size={15} />}
                  label="Message guest"
                />
              </>
            )}
            {details.email && (
              <GuestPreviewQuickActionButton
                url={`mailto:${details.email}`}
                icon={<Mail size={15} />}
                label="Email guest"
              />
            )}
          </div>
        </div>
      </div>
      <div className="ml-4 flex items-start gap-3">
        <div className="hidden text-right sm:block">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Lifetime Value
          </p>
          <p className="text-2xl font-bold text-emerald-700">
            {formatGuestPreviewCurrency(
              details.lifetime_value,
              details.currency,
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          aria-label="Edit client details"
          title="Edit client details"
        >
          <Pencil size={18} />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          aria-label="Close preview"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};
